import { burstLengthMs, validateProjectileLooks, type ProjectileLook, type ProjectileStrip } from "odyssey-shared";

/**
 * The projectile looks a page draws with (ADR-095 decision 19), served as creature sheets are: a pointer under the
 * asset base, `assets/projectiles/current.json`, lists each look with its strips' hashed paths, and each strip is a
 * sprite sheet texture of one row of frames. The pointer and the strips are loaded once, before the join, so a page
 * that arrives mid-flight draws the rest of it. Nothing here fails the page: a pointer that does not answer or does not
 * validate leaves the page with no looks, and a look with a strip that does not load, or whose image is not its
 * frames' size, is left out, each with one warning. The view draws nothing for a look this does not hold.
 */

/**
 * Where the pointer is, under the asset base.
 */
export const PROJECTILE_POINTER = "assets/projectiles/current.json";

/**
 * What of the texture manager the looks use.
 */
export interface StripTextures {
  exists(key: string): boolean;
  addSpriteSheet(key: string, source: HTMLImageElement, config: { frameWidth: number; frameHeight: number }): unknown;
}

/**
 * A look ready to draw: the look, the texture of its flight strip, the texture of each burst strip in order, and how
 * long its burst plays.
 */
export interface DrawnLook {
  readonly look: ProjectileLook;
  readonly flightKey: string;
  readonly burstKeys: readonly string[];
  readonly burstMs: number;
}

/**
 * A decoded image as the looks read it: its size, and itself for the texture.
 */
export type StripImage = HTMLImageElement;

/**
 * How the looks reach the network: the pointer's fetch and an image's load, each the page's own unless a test hands in
 * another.
 */
export interface LookSources {
  readonly fetcher: (url: string, init: RequestInit) => Promise<Response>;
  readonly image: (url: string) => Promise<StripImage>;
  readonly warn: (line: string) => void;
}

/**
 * Loads an image and decodes it before it is handed on, so its upload does not decode it in a frame.
 *
 * @param url The image's address.
 * @returns The decoded image.
 * @throws Error when it does not load or decode.
 */
async function loadImage(url: string): Promise<StripImage> {
  const image = new Image();
  image.crossOrigin = "anonymous";
  image.src = url;
  await image.decode();
  return image;
}

/**
 * The page's own sources.
 */
const PAGE_SOURCES: LookSources = { fetcher: (url, init) => fetch(url, init), image: loadImage, warn: (line) => console.warn(line) };

/**
 * The texture key of a look's strip. It carries the strip's path, which carries the hash of its bytes
 * (`buildProjectileLooks`), so a page that boots a world again without a reload makes a texture for a strip that has
 * changed and does not go on drawing the one it holds.
 *
 * @param id The look's id.
 * @param part `flight`, or a burst strip's index.
 * @param path The strip's path under the asset base.
 * @returns The key.
 */
export function stripTextureKey(id: string, part: "flight" | number, path: string): string {
  return `projectile:${id}:${part}:${path}`;
}

/**
 * The looks of one page.
 */
export class ProjectileLooks {
  private readonly held = new Map<string, DrawnLook>();
  private asked: Promise<void> | null = null;
  private settledYet = false;

  /**
   * Whether the load has ended, with looks or without.
   *
   * @returns True once `start` has settled.
   */
  public get settled(): boolean {
    return this.settledYet;
  }

  /**
   * The ids of the looks held, in the pointer's order.
   *
   * @returns The ids.
   */
  public ids(): string[] {
    return [...this.held.keys()];
  }

  /**
   * A look ready to draw.
   *
   * @param id The look's id, as an ability's shape names it.
   * @returns The look, or null when the page holds none of that id.
   */
  public get(id: string): DrawnLook | null {
    return this.held.get(id) ?? null;
  }

  /**
   * Loads the pointer and every look's strips. Asked again, it hands back the first load. It never rejects.
   *
   * @param textures Where the strips become textures.
   * @param base The asset base the page loads from.
   * @param sources The fetch, the image load and the warning; the page's own unless given.
   * @returns A promise that settles once every look is held or left out.
   */
  public start(textures: StripTextures, base: string, sources: LookSources = PAGE_SOURCES): Promise<void> {
    this.asked ??= this.load(textures, base, sources).then(() => {
      this.settledYet = true;
    });
    return this.asked;
  }

  /**
   * Reads the pointer and loads each look.
   *
   * @param textures Where the strips become textures.
   * @param base The asset base.
   * @param sources The fetch, the image load and the warning.
   */
  private async load(textures: StripTextures, base: string, sources: LookSources): Promise<void> {
    const url = `${base}/${PROJECTILE_POINTER}`;
    let looks: readonly ProjectileLook[];
    try {
      const response = await sources.fetcher(url, { cache: "no-cache" });
      if (!response.ok) {
        throw new Error(`responded ${response.status}`);
      }
      looks = Object.values(validateProjectileLooks(await response.json()).looks);
    } catch (error) {
      sources.warn(`no projectile is drawn this session: ${url} ${error instanceof Error ? error.message : String(error)}`);
      return;
    }
    const loaded = await Promise.all(looks.map((look) => this.loadLook(textures, base, look, sources)));
    for (const drawn of loaded) {
      if (drawn !== null) {
        this.held.set(drawn.look.id, drawn);
      }
    }
  }

  /**
   * Loads one look's strips into textures.
   *
   * @param textures Where the strips become textures.
   * @param base The asset base.
   * @param look The look.
   * @param sources The image load and the warning.
   * @returns The look ready to draw, or null, with one warning, when a strip did not load or is not its frames' size.
   */
  private async loadLook(textures: StripTextures, base: string, look: ProjectileLook, sources: LookSources): Promise<DrawnLook | null> {
    const parts: readonly { readonly key: string; readonly strip: ProjectileStrip }[] = [{ key: stripTextureKey(look.id, "flight", look.flight.path), strip: look.flight }, ...look.burst.map((strip, index) => ({ key: stripTextureKey(look.id, index, strip.path), strip }))];
    try {
      await Promise.all(
        parts.map(async ({ key, strip }) => {
          if (textures.exists(key)) {
            return;
          }
          const image = await sources.image(`${base}/${strip.path}`);
          if (image.width !== strip.frameWidth * strip.frames || image.height !== strip.frameHeight) {
            throw new Error(`${strip.path} is ${image.width} by ${image.height}, not ${strip.frames} frames of ${strip.frameWidth} by ${strip.frameHeight}`);
          }
          if (!textures.exists(key) && textures.addSpriteSheet(key, image, { frameWidth: strip.frameWidth, frameHeight: strip.frameHeight }) === null) {
            throw new Error(`${strip.path} did not become a texture`);
          }
        }),
      );
    } catch (error) {
      sources.warn(`projectile look ${look.id} is not drawn this session: ${error instanceof Error ? error.message : String(error)}`);
      return null;
    }
    return { look, flightKey: stripTextureKey(look.id, "flight", look.flight.path), burstKeys: look.burst.map((strip, index) => stripTextureKey(look.id, index, strip.path)), burstMs: burstLengthMs(look) };
  }
}
